import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit, FiTrash2, FiSearch, FiCalendar, FiPlus, FiX } from 'react-icons/fi';
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';

const StockPurchase = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { grampanchayatName, arthikVarshLabel } = location.state || {};

    const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'
    const [searchTerm, setSearchTerm] = useState('');
    const [purchases, setPurchases] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [stockItems, setStockItems] = useState([]);

    // Form State
    const initialFormState = {
        purchaseNo: '',
        transactionType: 'रोख', // 'रोख' (Cash) or 'वर्ग / ट्रान्सफर' (Transfer)
        voucherNo: '',
        purchaseDate: new Date().toISOString().split('T')[0],
        supplierId: '',
        supplierName: '',
        totalAmount: 0,
        remarks: '',
        // Item Entry State (Temporary)
        currentItem: {
            itemId: '',
            itemName: '',
            measurement: '',
            currentStock: 0,
            quantity: '',
            withTax: false,
            rate: '',
            amount: ''
        },
        items: [], // Array of added items
        // Transaction Details
        txVoucherNo: '',
        txDate: new Date().toISOString().split('T')[0],
        personName: '',
        resolutionNo: '',
        resolutionDate: '',
        accountLedger: '',
        accountHead: '',
        txAmount: ''
    };
    const [formData, setFormData] = useState(initialFormState);

    // Fetch Initial Data (Purchases, Suppliers, Stock Items)
    useEffect(() => {
        // Purchases
        const purchaseRef = ref(database, 'Grampanchayat/Panipuravtha/StockPurchases');
        onValue(purchaseRef, (snapshot) => {
            const data = snapshot.val();
            const loaded = [];
            if (data) {
                Object.keys(data).forEach(key => loaded.push({ id: key, ...data[key] }));
            }
            setPurchases(loaded);
        });

        // Suppliers
        const suppliersRef = ref(database, 'Grampanchayat/Panipuravtha/Suppliers');
        onValue(suppliersRef, (snapshot) => {
            const data = snapshot.val();
            const loaded = [];
            if (data) Object.keys(data).forEach(key => loaded.push({ id: key, ...data[key] }));
            setSuppliers(loaded);
        });

        // Stock Items
        const stockRef = ref(database, 'Grampanchayat/Panipuravtha/Stock');
        onValue(stockRef, (snapshot) => {
            const data = snapshot.val();
            const loaded = [];
            if (data) Object.keys(data).forEach(key => loaded.push({ id: key, ...data[key] }));
            setStockItems(loaded);
        });
    }, []);

    const handleAddItem = () => {
        if (!formData.currentItem.itemId || !formData.currentItem.quantity || !formData.currentItem.rate) {
            alert('कृपया वस्तू, संख्या आणि दर भरा');
            return;
        }
        
        const newItem = {
            ...formData.currentItem,
            total: parseFloat(formData.currentItem.amount) || 0
        };

        setFormData(prev => ({
            ...prev,
            items: [...prev.items, newItem],
            totalAmount: (parseFloat(prev.totalAmount) || 0) + newItem.total,
            currentItem: initialFormState.currentItem // Reset item entry
        }));
    };

    const handleRemoveItem = (index) => {
        const itemToRemove = formData.items[index];
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index),
            totalAmount: prev.totalAmount - itemToRemove.total
        }));
    };

    const handleSave = () => {
        if (!formData.supplierId || formData.items.length === 0) {
            alert('कृपया पुरवठादार निवडा आणि किमान एक वस्तू जोडा');
            return;
        }

        const purchaseRef = ref(database, 'Grampanchayat/Panipuravtha/StockPurchases');
        const newPurchase = {
            ...formData,
            createdAt: new Date().toISOString()
        };

        if (formData.id) {
            // Update logic here
        } else {
            push(purchaseRef, newPurchase)
                .then(() => {
                    alert('खरेदी व्यवहार यशस्वीरित्या साठवला');
                    setFormData(initialFormState);
                    setViewMode('list');
                })
                .catch(err => {
                    console.error(err);
                    alert('त्रुटी आली');
                });
        }
    };

    const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isLedgerModalOpen, setIsLedgerModalOpen] = useState(false);
    // Mock Ledgers if not available (ideally this should be fetched or passed as props, but creating a dummy list for now based on image structure)
    // or reusing existing data if available. For now using a placeholder empty list as per "No records found" in image unless implemented.
    const [ledgers, setLedgers] = useState([
        { id: 1, ledgerNo: '101', name: 'General Fund', group: 'Main' }, 
        { id: 2, ledgerNo: '102', name: 'Water Supply', group: 'Utility' }
    ]); // Mock data for functionality testing


    // ... (existing functions)

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6 font-poppins">
            {/* Modal */}
            {isSupplierModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl overflow-hidden animate-fade-in-up">
                        {/* Modal Header */}
                        <div className="bg-blue-800 text-white p-3 flex justify-between items-center">
                            <h3 className="font-bold text-lg">पुरवठादार यादी</h3>
                            <button onClick={() => setIsSupplierModalOpen(false)} className="text-white hover:text-red-300 transition-colors">
                                <FiX size={24} />
                            </button>
                        </div>
                        
                        {/* Modal Content */}
                        <div className="p-4">
                            {/* Search */}
                            <div className="flex items-center gap-2 mb-4 bg-white p-2 border border-blue-200 rounded">
                                <label className="font-bold text-gray-700 whitespace-nowrap">शोधणे</label>
                                <input 
                                    type="text" 
                                    placeholder="पुरवठादार क्र./ पुरवठादाराचे नाव भरा."
                                    className="w-full border border-gray-300 p-2 rounded outline-none focus:border-blue-500"
                                    // reused searchTerm or new local state? Using local state for modal would be better but for simplicity using a filtered list logic inline if needed, or better yet reuse the main suppliers list
                                />
                            </div>

                            {/* Table */}
                            <div className="overflow-x-auto h-96 overflow-y-auto border border-gray-300">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className="bg-[#2c5282] text-white sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2 border-r border-blue-400">क्रमांक</th>
                                            <th className="px-4 py-2 border-r border-blue-400">पुरवठादाराचे नाव</th>
                                            <th className="px-4 py-2 border-r border-blue-400">संपर्क नं.</th>
                                            <th className="px-4 py-2 border-r border-blue-400">खातावणी</th>
                                            <th className="px-4 py-2 border-r border-blue-400">खाते क्र.</th>
                                            <th className="px-4 py-2 border-r border-blue-400">GST नोंदणी</th>
                                            <th className="px-4 py-2">GST No.</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {suppliers.length > 0 ? (
                                            suppliers.map((s, idx) => (
                                                <tr 
                                                    key={s.id} 
                                                    className="hover:bg-blue-100 cursor-pointer transition-colors odd:bg-white even:bg-gray-50"
                                                    onClick={() => {
                                                        setFormData(prev => ({...prev, supplierId: s.id, supplierName: s.name}));
                                                        setIsSupplierModalOpen(false);
                                                    }}
                                                >
                                                    <td className="px-4 py-2 border-r">{s.supplierNo}</td>
                                                    <td className="px-4 py-2 border-r font-medium text-blue-800">{s.name}</td>
                                                    <td className="px-4 py-2 border-r">{s.contactNo}</td>
                                                    <td className="px-4 py-2 border-r">595</td>
                                                    <td className="px-4 py-2 border-r">{s.accountNo}</td>
                                                    <td className="px-4 py-2 border-r">{s.gstReg}</td>
                                                    <td className="px-4 py-2">-</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="p-4 text-center text-gray-500">No records found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination (Visual) */}
                            <div className="bg-gradient-to-b from-blue-700 to-blue-900 p-2 mt-0 flex justify-center gap-1">
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">|&lt;</button>
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">&lt;&lt;</button>
                                <button className="px-3 py-1 bg-teal-600 text-white rounded text-xs border border-teal-400 font-bold">1</button>
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">&gt;&gt;</button>
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">&gt;|</button>
                                <div className="ml-2 bg-white rounded flex items-center px-1">
                                    <select className="text-xs font-bold text-gray-700 outline-none border-none py-1">
                                        <option>5</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Item Selection Modal */}
            {isItemModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl overflow-hidden animate-fade-in-up">
                        {/* Modal Header */}
                        <div className="bg-blue-800 text-white p-3 flex justify-between items-center">
                            <h3 className="font-bold text-lg">वस्तु/मालाची यादी</h3>
                            <button onClick={() => setIsItemModalOpen(false)} className="text-white hover:text-red-300 transition-colors">
                                <FiX size={24} />
                            </button>
                        </div>
                        
                        {/* Modal Content */}
                        <div className="p-4">
                            {/* Search */}
                            <div className="flex items-center gap-2 mb-4 bg-white p-2 border border-blue-200 rounded">
                                <label className="font-bold text-gray-700 whitespace-nowrap">शोधणे</label>
                                <input 
                                    type="text" 
                                    placeholder="वस्तु नं./वस्तुचे नाव भरा."
                                    className="w-full border border-gray-300 p-2 rounded outline-none focus:border-blue-500"
                                />
                            </div>

                            {/* Table */}
                            <div className="overflow-x-auto h-96 overflow-y-auto border border-gray-300">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className="bg-[#2c5282] text-white sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2 border-r border-blue-400">वस्तु नं.</th>
                                            <th className="px-4 py-2 border-r border-blue-400">नाव</th>
                                            <th className="px-4 py-2 border-r border-blue-400">शिल्लक संख्या</th>
                                            <th className="px-4 py-2 border-r border-blue-400">प्रकार</th>
                                            <th className="px-4 py-2 border-r border-blue-400">HSN No</th>
                                            <th className="px-4 py-2">कर दर</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {stockItems.length > 0 ? (
                                            stockItems.map((item, idx) => (
                                                <tr 
                                                    key={item.id} 
                                                    className="hover:bg-blue-100 cursor-pointer transition-colors odd:bg-white even:bg-gray-50"
                                                    onClick={() => {
                                                        setFormData(prev => ({
                                                            ...prev, 
                                                            currentItem: {
                                                                ...prev.currentItem, 
                                                                itemId: item.id, 
                                                                itemName: item.name,
                                                                measurement: item.measurementType || '',
                                                                rate: item.rate || ''
                                                            }
                                                        }));
                                                        setIsItemModalOpen(false);
                                                    }}
                                                >
                                                    <td className="px-4 py-2 border-r">{item.objectNo}</td>
                                                    <td className="px-4 py-2 border-r font-medium text-blue-800">{item.name}</td>
                                                    <td className="px-4 py-2 border-r text-center font-bold">0</td>
                                                    <td className="px-4 py-2 border-r">{item.type}</td>
                                                    <td className="px-4 py-2 border-r">{item.hsnCode}</td>
                                                    <td className="px-4 py-2">{item.taxRate}%</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="p-4 text-center text-gray-500">No records found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination (Visual) */}
                            <div className="bg-gradient-to-b from-blue-700 to-blue-900 p-2 mt-0 flex justify-center gap-1">
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">|&lt;</button>
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">&lt;&lt;</button>
                                <button className="px-3 py-1 bg-teal-600 text-white rounded text-xs border border-teal-400 font-bold">1</button>
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">&gt;&gt;</button>
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">&gt;|</button>
                                <div className="ml-2 bg-white rounded flex items-center px-1">
                                    <select className="text-xs font-bold text-gray-700 outline-none border-none py-1">
                                        <option>10</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Ledger Selection Modal */}
            {isLedgerModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl overflow-hidden animate-fade-in-up">
                        {/* Modal Header */}
                        <div className="bg-blue-800 text-white p-3 flex justify-between items-center">
                            <h3 className="font-bold text-lg">खातावणी माहिती</h3>
                            <button onClick={() => setIsLedgerModalOpen(false)} className="text-white hover:text-red-300 transition-colors">
                                <FiX size={24} />
                            </button>
                        </div>
                        
                        {/* Modal Content */}
                        <div className="p-4">
                            {/* Search */}
                            <div className="flex items-center gap-2 mb-4 bg-white p-2 border border-blue-200 rounded">
                                <label className="font-bold text-gray-700 whitespace-nowrap">शोधणे</label>
                                <input 
                                    type="text" 
                                    placeholder="खातावणी क्र./ खातावणीचे नाव भरा."
                                    className="w-full border border-gray-300 p-2 rounded outline-none focus:border-blue-500"
                                />
                            </div>

                            {/* Table */}
                            <div className="overflow-x-auto h-96 overflow-y-auto border border-gray-300">
                                <table className="w-full text-sm text-left border-collapse">
                                    <thead className="bg-[#2c5282] text-white sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2 border-r border-blue-400">खातावणी नं.</th>
                                            <th className="px-4 py-2 border-r border-blue-400">खातावणीचे नाव</th>
                                            <th className="px-4 py-2">खाते गट</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {ledgers.length > 0 ? (
                                            ledgers.map((l, idx) => (
                                                <tr 
                                                    key={l.id} 
                                                    className="hover:bg-blue-100 cursor-pointer transition-colors odd:bg-white even:bg-gray-50"
                                                    onClick={() => {
                                                        setFormData(prev => ({...prev, ledgerId: l.id, ledgerName: l.name}));
                                                        setIsLedgerModalOpen(false);
                                                    }}
                                                >
                                                    <td className="px-4 py-2 border-r">{l.ledgerNo}</td>
                                                    <td className="px-4 py-2 border-r font-medium text-blue-800">{l.name}</td>
                                                    <td className="px-4 py-2">{l.group}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="3" className="p-4 text-center text-gray-500">No records found</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination (Visual) */}
                            <div className="bg-gradient-to-b from-blue-700 to-blue-900 p-2 mt-0 flex justify-center gap-1">
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">|&lt;</button>
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">&lt;&lt;</button>
                                <button className="px-3 py-1 bg-teal-600 text-white rounded text-xs border border-teal-400 font-bold">1</button>
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">&gt;&gt;</button>
                                <button className="px-3 py-1 bg-blue-600/50 text-white rounded text-xs border border-blue-400 disabled:opacity-50">&gt;|</button>
                                <div className="ml-2 bg-white rounded flex items-center px-1">
                                    <select className="text-xs font-bold text-gray-700 outline-none border-none py-1">
                                        <option>10</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg border border-blue-900 overflow-hidden">
                {/* Header */}
                <div className="bg-blue-800 text-white p-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                         <button 
                            onClick={() => viewMode === 'form' ? setViewMode('list') : navigate(-1)} 
                            className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors flex items-center justify-center mr-2"
                        >
                            <FiArrowLeft className="text-xl" />
                        </button>
                        <h1 className="text-xl font-bold">
                            {viewMode === 'list' ? 'साठा खरेदी माहिती' : 'साठा खरेदी व्यवहार माहिती'}
                        </h1>
                    </div>
                    {viewMode === 'list' && (
                        <button 
                            onClick={() => {
                                setFormData(initialFormState);
                                setViewMode('form');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-1.5 rounded font-bold shadow-sm transition-colors text-sm"
                        >
                            नवीन
                        </button>
                    )}
                </div>

                {viewMode === 'list' ? (
                    <>
                        {/* List View Details */}
                        <div className="p-4 border-b border-gray-200">
                             <div className="flex gap-4 items-center bg-gray-50 p-3 rounded border border-gray-200 mb-4">
                                <label className="font-medium text-gray-700">व्यवहार दिनांक:</label>
                                <div className="flex items-center border border-gray-300 rounded bg-white px-2">
                                    <input type="date" className="p-1 outline-none text-sm" defaultValue={new Date().toISOString().split('T')[0]} />
                                </div>
                                <button className="bg-green-600 text-white px-4 py-1 rounded text-sm font-bold">दाखवा</button>
                             </div>

                             <div className="flex gap-2">
                                <div className="relative flex-grow">
                                    <input 
                                        type="text" 
                                        placeholder="शोधणे" 
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-4 py-2 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700"
                                    />
                                </div>
                                <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-bold shadow-sm transition-colors">
                                    शोधणे
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-center border-collapse">
                                <thead className="bg-[#2c5282] text-white">
                                    <tr>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">खरेदी दिनांक</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium w-1/3 text-left">पुरवठादाराचे नाव</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">बिल क्र.</th>
                                        <th className="px-4 py-3 border-r border-blue-400 font-medium">एकूण रक्कम</th>
                                        <th className="px-4 py-3 font-medium">पर्याय</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-300">
                                    {purchases.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-left py-4 px-4 text-gray-700 font-medium bg-white">No records found</td>
                                        </tr>
                                    ) : (
                                        purchases.map(item => (
                                            <tr key={item.id} className="hover:bg-blue-50">
                                                <td className="px-4 py-3 border-r">{item.purchaseDate}</td>
                                                <td className="px-4 py-3 border-r text-left">{item.supplierName}</td>
                                                <td className="px-4 py-3 border-r">{item.voucherNo}</td>
                                                <td className="px-4 py-3 border-r">{item.totalAmount}</td>
                                                <td className="px-4 py-3">
                                                    <button className="bg-blue-700 text-white px-3 py-1 rounded text-xs mr-2">बदल</button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination */}
                        <div className="bg-gradient-to-b from-blue-700 to-blue-900 p-2 flex justify-center gap-1">
                             <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs border border-blue-400">|&lt;</button>
                             <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs border border-blue-400">&lt;&lt;</button>
                             <button className="px-3 py-1 bg-teal-600 text-white rounded text-xs border border-teal-400 font-bold">1</button>
                             <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs border border-blue-400">&gt;&gt;</button>
                             <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs border border-blue-400">&gt;|</button>
                        </div>
                    </>
                ) : (
                    // FORM VIEW
                    <div className="p-4 bg-gray-50">
                        {/* Header Box */}
                        <div className="border border-blue-300 rounded p-4 bg-white mb-4 text-sm">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                <div className="flex items-center gap-2">
                                    <label>खरेदी क्र.:</label>
                                    <input type="text" value="खरेदी" disabled className="border p-1 rounded w-20 bg-gray-100" />
                                </div>
                                <div className="flex items-center gap-2 col-span-2">
                                    <label>व्यवहार प्रकार</label>
                                    <div className="flex gap-4">
                                        <label className="flex items-center gap-1"><input type="radio" name="ttype" checked /> रोख</label>
                                        <label className="flex items-center gap-1"><input type="radio" name="ttype" /> वर्ग / ट्रान्सफर</label>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <label>वौचर नं.:</label>
                                    <input type="text" className="border p-1 rounded w-full" placeholder="वौचर नं. भरा" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label>खरेदी दिनांक:</label>
                                    <input type="date" className="border p-1 rounded w-full" />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="w-32">पुरवठादाराचे नाव:</label>
                                <div className="flex-grow flex gap-0">
                                    <input 
                                        type="text"
                                        className="w-full border border-blue-300 p-1.5 rounded-l outline-none bg-white"
                                        value={formData.supplierName}
                                        placeholder="पुरवठादार निवडा"
                                        readOnly
                                    />
                                    <button 
                                        onClick={() => setIsSupplierModalOpen(true)}
                                        className="bg-blue-800 text-white px-3 rounded-r hover:bg-blue-900 transition-colors"
                                    >
                                        ...
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Main Content: Split View */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                            {/* Left: Item Entry Form */}
                            <div className="lg:col-span-4 border border-blue-500 rounded flex flex-col">
                                <div className="bg-blue-800 text-white p-2 text-center font-bold text-sm">वस्तुची माहिती</div>
                                <div className="p-4 bg-white flex-grow flex flex-col gap-3 text-sm">
                                    <div>
                                        <label className="block mb-1 text-gray-700">वस्तुचे नाव:</label>
                                        <div className="flex">
                                            <input 
                                                type="text"
                                                className="w-full border border-gray-300 p-1.5 rounded-l bg-white"
                                                value={formData.currentItem.itemName}
                                                placeholder="वस्तुचे नाव निवडा."
                                                readOnly
                                            />
                                            <button 
                                                onClick={() => setIsItemModalOpen(true)}
                                                className="bg-blue-800 text-white px-3 rounded-r hover:bg-blue-900 transition-colors"
                                            >
                                                ...
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <label className="block mb-1 text-gray-700">मोजमाप प्रकार:</label>
                                        <input type="text" className="w-full border border-gray-300 p-1.5 rounded bg-gray-50" readOnly value={formData.currentItem.measurement} />
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-gray-700">शिल्लक संख्या:</label>
                                        <div className="bg-blue-100 border border-blue-300 p-1.5 text-center font-bold text-blue-800">0</div>
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-gray-700">संख्या:</label>
                                        <input 
                                            type="number" 
                                            className="w-full border border-blue-300 p-1.5 rounded" 
                                            placeholder="संख्या भरा." 
                                            value={formData.currentItem.quantity}
                                            onChange={(e) => {
                                                const qty = parseFloat(e.target.value) || 0;
                                                const rate = parseFloat(formData.currentItem.rate) || 0;
                                                setFormData(prev => ({
                                                    ...prev, 
                                                    currentItem: { ...prev.currentItem, quantity: e.target.value, amount: qty * rate }
                                                }));
                                            }}
                                        />
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <label className="text-gray-700">करा सहित:</label>
                                        <input type="checkbox" className="w-4 h-4" />
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-gray-700">दर:</label>
                                        <input 
                                            type="number" 
                                            className="w-full border border-blue-300 p-1.5 rounded" 
                                            placeholder="दर भरा." 
                                            value={formData.currentItem.rate}
                                            onChange={(e) => {
                                                const rate = parseFloat(e.target.value) || 0;
                                                const qty = parseFloat(formData.currentItem.quantity) || 0;
                                                setFormData(prev => ({
                                                    ...prev, 
                                                    currentItem: { ...prev.currentItem, rate: e.target.value, amount: qty * rate }
                                                }));
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <label className="block mb-1 text-gray-700">रक्कम:</label>
                                        <input 
                                            type="text" 
                                            className="w-full border border-gray-300 p-1.5 rounded bg-gray-100" 
                                            placeholder="रक्कम." 
                                            readOnly
                                            value={formData.currentItem.amount}
                                        />
                                    </div>

                                    <button 
                                        onClick={handleAddItem}
                                        className="mt-2 bg-gradient-to-r from-green-600 to-green-700 text-white p-2 rounded font-bold shadow hover:from-green-700 hover:to-green-800 transition-all"
                                    >
                                        यादित भरा
                                    </button>
                                </div>
                            </div>

                            {/* Right: Items Table */}
                            <div className="lg:col-span-8 border border-blue-500 rounded overflow-hidden flex flex-col">
                                <div className="bg-[#2c5282] text-white flex text-xs font-bold p-2">
                                    <div className="w-8">#</div>
                                    <div className="flex-grow">वस्तुचे नाव</div>
                                    <div className="w-20 text-center">मोजमाप</div>
                                    <div className="w-16 text-center">संख्या</div>
                                    <div className="w-20 text-center">दर</div>
                                    <div className="w-20 text-center">एकूण</div>
                                    <div className="w-20 text-center">कर रक्कम</div>
                                    <div className="w-8">#</div>
                                    <div className="w-20 text-center">एकूण</div>
                                </div>
                                <div className="bg-gray-100 flex-grow p-2">
                                    {formData.items.length === 0 ? (
                                        <div className="text-gray-500 text-sm italic">No records found</div>
                                    ) : (
                                        formData.items.map((item, idx) => (
                                            <div key={idx} className="flex text-sm p-2 border-b border-gray-300 bg-white">
                                                <div className="w-8">{idx + 1}</div>
                                                <div className="flex-grow">{item.itemName}</div>
                                                <div className="w-20 text-center">{item.measurement}</div>
                                                <div className="w-16 text-center">{item.quantity}</div>
                                                <div className="w-20 text-center">{item.rate}</div>
                                                <div className="w-20 text-center">{item.amount}</div>
                                                <div className="w-20 text-center">0.00</div>
                                                <div className="w-8 text-red-500 cursor-pointer" onClick={() => handleRemoveItem(idx)}><FiTrash2 /></div>
                                                <div className="w-20 text-center">{item.amount}</div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                {/* Totals Row */}
                                <div className="bg-gray-200 border-t border-gray-300 p-2 flex justify-between text-sm font-bold">
                                    <span>एकूण</span>
                                    <div className="flex gap-4">
                                        <span>0.00</span>
                                        <span>0.00</span>
                                        <span className="mr-8">{formData.totalAmount.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bottom: Transaction Details */}
                        <div className="mt-4 border-t border-blue-800 bg-blue-50 p-2 text-sm font-bold text-blue-900">
                             व्यवहार तपशील
                        </div>
                        <div className="border border-blue-300 bg-white p-4 text-sm">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">वौचर नं. :</label>
                                    <input type="text" className="col-span-2 border p-1 rounded" placeholder="भरा." />
                                </div>
                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">दिनांक :</label>
                                    <input type="date" className="col-span-2 border p-1 rounded" />
                                </div>
                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">व्यक्तीचे नाव :</label>
                                    <input type="text" className="col-span-2 border p-1 rounded bg-gray-200" disabled value="व्यक्तीचे नाव भरा." />
                                </div>
                                <div className="col-span-2"></div> 
                                
                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">ठराव नं. :</label>
                                    <input type="text" className="col-span-2 border p-1 rounded" placeholder="ठराव नं. भरा." />
                                </div>
                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">ठराव दिनांक:</label>
                                    <input type="date" className="col-span-2 border p-1 rounded" />
                                </div>

                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">खातावणी :</label>
                                    <div className="col-span-2 flex gap-1">
                                        <input 
                                            type="text"
                                            className="flex-grow border p-1 rounded bg-white"
                                            value={formData.ledgerName || ''}
                                            placeholder="खातावणी निवडा"
                                            readOnly
                                        />
                                        <button 
                                            onClick={() => setIsLedgerModalOpen(true)}
                                            className="bg-blue-800 text-white px-2 rounded hover:bg-blue-900 transition-colors"
                                        >
                                            ...
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">वाटपची रक्कम :</label>
                                    <input type="text" className="col-span-2 border p-1 rounded bg-gray-200" value="budgetAmount" disabled/>
                                </div>

                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">खाते :</label>
                                    <div className="col-span-2 flex gap-1">
                                        <select className="flex-grow border p-1 rounded bg-gray-200"><option>...</option></select>
                                        <button className="bg-blue-800 text-white px-2 rounded">...</button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">खाते शिल्लक :</label>
                                    <input type="text" className="col-span-2 border p-1 rounded bg-gray-200" value="balanceAmount" disabled/>
                                </div>

                                <div className="grid grid-cols-3 gap-2 items-center">
                                    <label className="text-right">व्यवहार रक्कम :</label>
                                    <input type="text" className="col-span-2 border p-1 rounded bg-gray-200" placeholder="रक्कम भरा." disabled value={formData.totalAmount.toFixed(2)} />
                                </div>
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div className="flex justify-center gap-4 mt-4 bg-blue-900 p-3 rounded-b-lg text-white">
                            <button className="bg-green-600 px-6 py-1 rounded font-bold" onClick={handleSave}>साठविणे</button>
                            <button className="bg-red-500 px-6 py-1 rounded font-bold" onClick={() => setViewMode('list')}>रद्द करणे</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StockPurchase;
